import { useState, useEffect, useMemo } from 'react';
import { posthog } from 'posthog-js';

type Result<T> = [boolean, boolean, T | null];

/**
 * Hook to check if a feature is enabled in PostHog.
 * 
 * @param feature - The feature to check.
 * @returns A tuple containing the feature state, a boolean indicating if the feature state has been loaded, and the feature payload.
 * 
 * @example
 * const [hasFeature, isReady, featurePayload] = useHasPosthogFeature('feature-name');
 * if (isReady) {
 *   if (hasFeature) {
 *     // The feature is enabled
 *     console.log(featurePayload);
 *   }
 * }
 */
const useHasPosthogFeature = <T extends Record<string, any> = Record<string, any>>(feature: string): Result<T> => {
  const [flagResult, setFlagResult] = useState<{ hasFeature: boolean, value: T } | null>(null);

  useEffect(() => {
    const unsubscribe = posthog.onFeatureFlags(() => {
      const isEnabled = posthog.isFeatureEnabled(feature);
      const featureValue = posthog.getFeatureFlagPayload(feature);
      setFlagResult({ hasFeature: !!isEnabled, value: featureValue as T });
    });

    return () => {
      unsubscribe();
    };
  }, [feature]);

  const result = useMemo(() => {
    const flag = !!flagResult?.hasFeature;
    const ready = flagResult !== null;
    const value = flagResult?.value as T;
    return [flag, ready, value] as Result<T>;
  }, [flagResult]);
  
  return result;
};
export default useHasPosthogFeature;
